import { Vehicle } from '../inventory'
import { PrivateUserInfoType } from '../user'

export type DealApprovalStageType = 'availability' | 'condition' | 'title' | 'final'

/** Status of a single stage: approved, rejected, or conditionally approved (same as approved but with a note). */
export type StageDecisionStatus = 'approved' | 'rejected' | 'conditionally_approved'

/** Decision at one approval stage (availability, condition, title, or final). */
export interface StageDecision {
  user_id: string
  status: StageDecisionStatus
  note?: string
  at?: string // ISO timestamp
}

/** Per-vehicle decisions: one optional sub-decision per stage. */
export interface VehicleDecision {
  availability?: StageDecision
  condition?: StageDecision
  title?: StageDecision
  final?: StageDecision
}

/** Derived vehicle-level status for UI/tabs (pending = in progress). */
export type VehicleDecisionStatus = 'pending' | 'approved' | 'conditionally_approved' | 'rejected'

/** Summary for email/display (same order as vehicle_ids) */
export interface VehicleSummary {
  stock_number: string
  vin: string
}

export interface DealApproval {
  id: string
  created_by: string // User ID who created the deal
  created_by_name?: string // Name of user who created the deal
  created_at: string // ISO date string
  updated_at: string // ISO date string
  vehicle_ids: string[] // Array of vehicle IDs in the deal
  /** Stock # and VIN per vehicle (same order as vehicle_ids), set at creation for notifications */
  vehicle_summaries?: VehicleSummary[]
  vehicles?: Vehicle[] // Optional: populated vehicles data
  /** Per-vehicle decisions by stage (key = vehicle id) */
  vehicle_decisions?: Record<string, VehicleDecision>
  notes?: string // Optional notes about the deal
}

/** Get the decision for a stage. */
export function getStageDecision(
  d: VehicleDecision | undefined,
  stage: DealApprovalStageType
): StageDecision | undefined {
  if (!d) return undefined
  return d[stage]
}

/** True if all four stages have a decision (approved, conditionally_approved, or rejected). */
export function vehicleDecisionHasAllStages(d: VehicleDecision | undefined): boolean {
  if (!d) return false
  return Boolean(d.availability && d.condition && d.title && d.final)
}

/** First stage that is rejected, if any. */
export function getRejectedStage(d: VehicleDecision | undefined): DealApprovalStageType | undefined {
  if (!d) return undefined
  if (d.availability?.status === 'rejected') return 'availability'
  if (d.condition?.status === 'rejected') return 'condition'
  if (d.title?.status === 'rejected') return 'title'
  if (d.final?.status === 'rejected') return 'final'
  return undefined
}

/** Stages that are conditionally approved (have a note). */
export function getConditionallyApprovedStages(
  d: VehicleDecision | undefined
): DealApprovalStageType[] {
  if (!d) return []
  const out: DealApprovalStageType[] = []
  if (d.availability?.status === 'conditionally_approved') out.push('availability')
  if (d.condition?.status === 'conditionally_approved') out.push('condition')
  if (d.title?.status === 'conditionally_approved') out.push('title')
  if (d.final?.status === 'conditionally_approved') out.push('final')
  return out
}

/** Derived vehicle-level status for tabs/UI. */
export function getVehicleDecisionStatus(
  d: VehicleDecision | undefined
): VehicleDecisionStatus {
  if (!d) return 'pending'
  const rejected = getRejectedStage(d)
  if (rejected) return 'rejected'
  if (!vehicleDecisionHasAllStages(d)) return 'pending'
  const hasConditional = getConditionallyApprovedStages(d).length > 0
  return hasConditional ? 'conditionally_approved' : 'approved'
}
